<script lang="ts" setup>
import { computed, ref, watch, onBeforeUnmount } from "vue";
import { useGetDocumentPagesByDocumentIdQuery } from "@/graphql/operations";
import { LoadingSpinner, Thumbnail } from "@/components";
import { useRevisionChecker } from "@/composables";

const props = defineProps({
  documentId: {
    type: String,
    required: true,
  },
});

const emits = defineEmits(["change"]);

const { data, fetching, executeQuery } = useGetDocumentPagesByDocumentIdQuery({
  variables: {
    documentId: props.documentId,
  },
  requestPolicy: "cache-and-network",
});

const { isProcessing } = useRevisionChecker(computed(() => props.documentId));

const pages = computed(
  () =>
    data.value?.document?.documentRevisionByIdAndCurrentRevisionNumber
      ?.documentPagesByDocumentIdAndRevisionNumber?.nodes || [],
);
const selectedPages = ref<Array<string>>([]);

// Preserve the document's page order, independently of selection order or IDs.
const emitSelection = () => {
  emits(
    "change",
    pages.value
      .filter((page) => page && selectedPages.value.includes(page.id))
      .map((page) => page!.id),
  );
};

const selectPage = (pageId: string) => {
  if (selectedPages.value.includes(pageId)) {
    selectedPages.value = selectedPages.value.filter((page) => page !== pageId);
    emitSelection();
  } else {
    selectedPages.value.push(pageId);
    emitSelection();
  }
};

let intervalId: number | undefined = window.setInterval(() => {
  executeQuery();
}, 2000);

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = undefined;
  }
});
</script>

<template>
  <div class="message info" v-if="isProcessing">
    {{ $t("documentPagePicker.processing") }}
  </div>

  <div v-if="fetching">
    <loading-spinner :color="'var(--classic-text, black)'" />
  </div>

  <div v-if="!fetching && !isProcessing" class="content">
    <div v-for="(page, idx) in pages" :key="page!.id">
      <thumbnail
        :pageId="page!.id"
        :class="{ thumbnail: true, selected: selectedPages.includes(page!.id) }"
        @click="selectPage(page!.id)"
        :data-test="`page-${idx}`"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
$pageWidth: 270px;
.content {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 10px;
  justify-items: center;

  .thumbnail {
    :deep(img) {
      max-width: $pageWidth;
      max-height: $pageWidth;
      object-fit: contain;
      border: 1px solid transparent;

      &:hover {
        cursor: pointer;
      }
    }

    &.selected {
      :deep(img) {
        object-fit: contain;
        border: 1px solid var(--classic-accent, #35b6ba);
        box-shadow: 0 0 0 3px var(--classic-accent, #35b6ba);
        border-radius: 0.35em;
      }
    }
  }
}
</style>
